import React, { useMemo, useState } from 'react';
import useGameBase from '../../hooks/useGameBase';

function formatPoints(value) {
  if (typeof value !== 'number') return '0';
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function buildStarPoints(boardSize) {
  if (boardSize === 13) {
    return new Set(['3,3', '3,9', '6,6', '9,3', '9,9']);
  }
  return new Set(['2,2', '2,6', '4,4', '6,2', '6,6']);
}

function getStatusMessage({ finished, finishReason, winningColor, currentPlayerName, currentTurnColor, consecutivePasses }) {
  if (finished) {
    if (finishReason === 'resignation') {
      return `${winningColor === 'B' ? 'Black' : 'White'} wins by resignation.`;
    }
    if (!winningColor) {
      return 'The game finished in a draw.';
    }
    return `${winningColor === 'B' ? 'Black' : 'White'} wins after scoring.`;
  }

  const turnLabel = currentTurnColor === 'W' ? 'White' : 'Black';
  const passLabel = consecutivePasses === 1 ? ' One pass has been made.' : '';
  return `${currentPlayerName || turnLabel} to play as ${turnLabel}.${passLabel}`;
}

export default function GoGame({ gameState = {}, gamePlayers = [], user = {}, socket, roomId }) {
  const { submitAction, isLocalPlayerTurn, findPlayerById } = useGameBase({ gameState, gamePlayers, user, socket, roomId });
  const boardSize = gameState.boardSize || 9;
  const board = gameState.board || Array.from({ length: boardSize }, () => Array(boardSize).fill(null));
  const [notice, setNotice] = useState('');

  const currentPlayer = findPlayerById(gameState.currentPlayer);
  const currentPlayerName = currentPlayer?.username || null;
  const myTurn = isLocalPlayerTurn();
  const localPlayerId = user?.socketId || user?.userId || user?.id || null;
  const localColor = gameState.playerColors?.[user?.socketId] || gameState.playerColors?.[user?.userId] || null;
  const starPoints = useMemo(() => buildStarPoints(boardSize), [boardSize]);
  const canAct = !gameState.finished && myTurn;

  const playersByColor = useMemo(() => {
    const black = findPlayerById(gameState.playersByColor?.B);
    const white = findPlayerById(gameState.playersByColor?.W);
    return { B: black, W: white };
  }, [findPlayerById, gameState.playersByColor]);

  const roundWins = useMemo(() => {
    const map = Object.fromEntries((gameState.players || []).map((player) => [player.socketId, player.score || 0]));
    return {
      B: gameState.playersByColor?.B ? (map[gameState.playersByColor.B] || 0) : 0,
      W: gameState.playersByColor?.W ? (map[gameState.playersByColor.W] || 0) : 0,
    };
  }, [gameState.players, gameState.playersByColor]);

  const statusMessage = getStatusMessage({
    finished: !!gameState.finished,
    finishReason: gameState.finishReason,
    winningColor: gameState.winningColor,
    currentPlayerName,
    currentTurnColor: gameState.currentTurnColor,
    consecutivePasses: gameState.consecutivePasses || 0,
  });

  const handleActionResponse = (response) => {
    if (!response) return;
    const messages = {
      occupied: 'That point is already occupied.',
      suicide: 'That move would be suicide.',
      ko: 'That move breaks the ko rule.',
      'not-your-turn': 'Wait for your turn.',
      'out-of-bounds': 'That move is outside the board.',
      'invalid-point': 'That point is not valid.',
    };
    if (response.status && messages[response.status]) {
      setNotice(messages[response.status]);
      window.setTimeout(() => setNotice(''), 1800);
    }
  };

  const placeStone = (row, col) => {
    if (!canAct || board[row][col]) return;
    submitAction({ type: 'place', row, col }).then(handleActionResponse).catch(() => {});
  };

  const passTurn = () => {
    if (!canAct) return;
    submitAction({ type: 'pass' }).then(handleActionResponse).catch(() => {});
  };

  const resignGame = () => {
    if (gameState.finished || !localPlayerId) return;
    submitAction({ type: 'resign' }).then(handleActionResponse).catch(() => {});
  };

  const boardPaddingClass = boardSize === 13 ? 'p-3 sm:p-4' : 'p-4 sm:p-5';

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-4 text-gray-900 dark:text-gray-100">
      <div className="rounded-2xl border border-amber-200 bg-white/90 p-4 shadow-sm dark:border-amber-900/70 dark:bg-gray-900/90">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            
            <div className="mt-1 text-lg font-semibold">{statusMessage}</div>
            <div className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              White Komi: {formatPoints(gameState.komi || 6.5)} 
            </div>
            {notice ? <div className="mt-2 text-sm font-medium text-red-600 dark:text-red-300">{notice}</div> : null}
          </div>

          <div className="flex  gap-2 md:justify-end">
            <button
              type="button"
              onClick={passTurn}
              disabled={!canAct}
              className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white dark:text-gray-900 transition hover:bg-amber-500 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-amber-500 dark:hover:bg-amber-400"
            >
              Pass
            </button>
            <button
              type="button"
              onClick={resignGame}
              disabled={gameState.finished}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
            >
              Resign
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <div className="rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-100 via-amber-50 to-orange-100 p-2 shadow-sm dark:border-amber-900/70 dark:from-amber-900/40 dark:via-gray-900 dark:to-orange-900/30 sm:p-3">
          <div
            className={`mx-auto grid aspect-square w-full max-w-[min(92vw,44rem)] touch-manipulation rounded-2xl border border-amber-700/40 bg-amber-300 shadow-inner dark:border-amber-500/30 dark:bg-amber-700/80 ${boardPaddingClass}`}
            style={{
              gridTemplateColumns: `repeat(${boardSize}, minmax(0, 1fr))`,
            }}
          >
            {board.map((row, rowIndex) => row.map((cell, colIndex) => {
              const isStarPoint = !cell && starPoints.has(`${rowIndex},${colIndex}`);
              const isLastMove = gameState.lastMove && gameState.lastMove.row === rowIndex && gameState.lastMove.col === colIndex;
              // Only draw lines if not at the far edge
              const showRight = colIndex < boardSize - 1;
              const showDown = rowIndex < boardSize - 1;
              return (
                <button
                  key={`${rowIndex}-${colIndex}`}
                  type="button"
                  onClick={() => placeStone(rowIndex, colIndex)}
                  disabled={!canAct || !!cell}
                  className="relative flex aspect-square items-center justify-center disabled:cursor-default"
                  aria-label={`Row ${rowIndex + 1}, column ${colIndex + 1}`}
                >
                  {/* Vertical line (down) */}
                  {showDown && (
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 bg-amber-900/80 dark:bg-amber-100/70"
                      style={{
                        height: '100%',
                        width: '2px',
                        transform: 'translate(-50%, 0)',
                      }}
                    />
                  )}
                  {/* Horizontal line (right) */}
                  {showRight && (
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute top-1/2 left-1/2 -translate-y-1/2 bg-amber-900/80 dark:bg-amber-100/70"
                      style={{
                        width: '100%',
                        height: '2px',
                        transform: 'translate(0, -50%)',
                      }}
                    />
                  )}
                  {/* Star point */}
                  {isStarPoint ? (
                    <span className="pointer-events-none absolute z-[1] h-2 w-2 rounded-full bg-amber-900/85 dark:bg-amber-100/75" />
                  ) : null}
                  {/* Stone */}
                  {cell ? (
                    <span
                      className={`relative z-10 block h-[74%] w-[74%] rounded-full ${cell === 'B' ? 'bg-gray-950 shadow-[inset_-6px_-7px_12px_rgba(255,255,255,0.08),0_6px_14px_rgba(0,0,0,0.3)]' : 'bg-gray-50 shadow-[inset_-8px_-8px_12px_rgba(0,0,0,0.10),0_6px_14px_rgba(0,0,0,0.22)]'}`}
                    >
                      {isLastMove ? (
                        <span className={`absolute inset-[18%] rounded-full border-2 ${cell === 'B' ? 'border-amber-300' : 'border-amber-700'}`} />
                      ) : null}
                    </span>
                  ) : null}
                </button>
              );
            }))}
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          {['B', 'W'].map((color) => {
            const player = playersByColor[color];
            const active = !gameState.finished && gameState.currentTurnColor === color;
            const local = localColor === color;
            return (
              <div key={color} className={`rounded-2xl border bg-white/90 p-4 shadow-sm dark:bg-gray-600/90 ${active ? 'border-amber-400 dark:border-amber-500' : 'border-gray-200 dark:border-gray-800'}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className={`block h-5 w-5 rounded-full border ${color === 'B' ? 'border-gray-900 bg-gray-950' : 'border-gray-400 bg-gray-50'}`} />
                    <div>
                      <div className="font-semibold text-gray-900 dark:text-gray-100">{player?.username || (color === 'B' ? 'Black' : 'White')}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {color === 'B' ? 'Black stones' : 'White stones'}{local ? ' • You' : ''}
                      </div>
                    </div>
                  </div>
                  {active ? <div className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-700 dark:text-amber-300">Turn</div> : null}
                </div>
                <div className="mt-3 flex items-center justify-between text-sm text-gray-700 dark:text-gray-300">
                  <div>Captures: {gameState.captures?.[color] || 0}</div>
                  <div>Round wins: {roundWins[color]}</div>
                </div>
              </div>
            );
          })}
        </div>

        
      </div>
    </div>
  );
}

GoGame.Guide = function GoGuide() {
  return (
    <div className="space-y-3">
      <h3 className="text-lg font-bold">How to Play Go</h3>
      <p>Players alternate placing stones on empty intersections. Black always plays first, then White.</p>
      <p>Any connected group with no liberties is captured and removed. Suicide moves are rejected.</p>
      <p>If both players pass in succession, the round ends and the board is scored by area: stones on the board plus surrounded territory, with komi added to White.</p>
      <p>This version supports 9x9 and 13x13 boards and includes a simple ko rule that blocks immediate board repetition.</p>
      <p>Komi is a set number of points added to White's score to compensate for going second.</p>
    </div>
  );
};